import React from 'react';
import { Eye, Mail } from 'lucide-react';
import FormModal from './FormModal';

interface EmailTemplate {
  id: number;
  template_key: string;
  name: string;
  category: 'system' | 'compliance' | 'expiry' | 'notification' | 'marketing' | 'custom';
  subject: string;
  html_content: string;
  text_content: string | null;
  variables: string[];
  required_variables: string[] | null;
  is_active: boolean;
  is_system: boolean;
  is_editable: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

interface EmailTemplatePreviewModalProps {
  onClose: () => void;
  template: EmailTemplate;
}

export const EmailTemplatePreviewModal: React.FC<EmailTemplatePreviewModalProps> = ({
  onClose,
  template,
}) => {
  return (
    <FormModal
      title={`Preview: ${template.name}`}
      subtitle="See how your email will look"
      icon={<Eye className="text-white" size={24} />}
      onSubmit={() => {}} // No submit action for preview
      onCancel={onClose}
      cancelText="Close"
      showSubmitButton={false}
      size="large"
    >
      <div className="space-y-6">
        <div className="bg-slate-50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Mail className="text-blue-600" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">{template.name}</h3>
              <p className="text-slate-600 mb-4">{template.template_key}</p>

              <div className="flex items-center gap-4 text-sm text-slate-500 mb-4">
                <span className="flex items-center gap-1">
                  <span className="font-medium">Category:</span> {template.category}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  template.is_active
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-700'
                }`}>
                  {template.is_active ? 'Active' : 'Inactive'}
                </span>
                {template.is_system && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                    System
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">Subject Line</h4>
          <p className="text-slate-900 text-lg font-medium bg-slate-100 rounded-lg p-4 border border-slate-200">
            {template.subject}
          </p>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <h4 className="text-lg font-semibold text-slate-900 mb-4">HTML Preview</h4>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <iframe
              srcDoc={template.html_content}
              className="w-full h-96 border-0 bg-white"
              title="Email Preview"
            />
          </div>
        </div>

        {template.text_content && (
          <div className="border-t border-slate-200 pt-6">
            <h4 className="text-lg font-semibold text-slate-900 mb-4">Text Version</h4>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h5 className="text-sm font-medium text-slate-700 mb-3">Plain Text Alternative</h5>
              <pre className="bg-white rounded-lg p-4 text-sm whitespace-pre-wrap border border-slate-200 font-mono">
                {template.text_content}
              </pre>
            </div>
          </div>
        )}

        {template.variables && template.variables.length > 0 && (
          <div className="border-t border-slate-200 pt-6">
            <h4 className="text-lg font-semibold text-slate-900 mb-4">Available Variables</h4>
            <div className="flex flex-wrap gap-2">
              {template.variables.map(variable => (
                <span key={variable} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium">
                  &#123;{variable}&#125;
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};